// src/services/dashboardService.js
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  limit,
} from 'firebase/firestore';
import { db } from './firebase';

/* ============================================================
   HELPERS
============================================================ */

// YYYY-MM independente de Timestamp / Date / string
function getMonthKey(dateValue) {
  try {
    let date;
    if (!dateValue) return null;

    if (dateValue instanceof Timestamp) {
      date = dateValue.toDate();
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === 'string') {
      date = new Date(
        dateValue.includes('T') ? dateValue : `${dateValue}T12:00:00`
      );
    } else if (
      typeof dateValue === 'object' &&
      typeof dateValue.toDate === 'function'
    ) {
      date = dateValue.toDate();
    } else {
      return null;
    }

    if (isNaN(date.getTime())) return null;

    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  } catch {
    return null;
  }
}

function toDateSafe(v) {
  if (!v) return null;

  if (v instanceof Timestamp) return v.toDate();
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;

  if (typeof v === 'object' && typeof v.toDate === 'function') {
    const d = v.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof v === 'string') {
    const s = v.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const d = new Date(`${s}T12:00:00`);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(s.includes('T') ? s : `${s}T12:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toTimestamp(v) {
  if (!v) return Timestamp.fromDate(new Date());
  if (v instanceof Timestamp) return v;
  if (typeof v === 'object' && typeof v.toDate === 'function') return v;

  const d = toDateSafe(v);
  return Timestamp.fromDate(d || new Date());
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function diffDays(a, b) {
  const A = startOfDay(a).getTime();
  const B = startOfDay(b).getTime();
  return Math.round((B - A) / (1000 * 60 * 60 * 24));
}

function calcDelta(atual, anterior) {
  const a = Number(atual || 0);
  const b = Number(anterior || 0);
  const delta = a - b;

  let deltaPct = 0;
  if (b === 0) deltaPct = a > 0 ? 100 : 0;
  else deltaPct = (delta / b) * 100;

  return {
    atual: a,
    anterior: b,
    delta,
    deltaPct: Number(deltaPct.toFixed(1)),
    direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat',
  };
}

/**
 * ✅ Período global para sincronizar dados por range
 * mode:
 *  - 'year'   -> Jan/Dez do ano selecionado (ou ano atual)
 *  - 'last12' -> últimos 12 meses (janela móvel)
 *  - 'month'  -> mês atual
 *  - 'custom' -> range definido (from/to)
 *
 * + NOVOS:
 *  - 'today'
 *  - 'last7'
 *  - 'last3'
 */
export function buildPeriod({ mode = 'year', year, from, to } = {}) {
  const now = new Date();

  if (mode === 'today') {
    return {
      mode,
      de: toTimestamp(startOfDay(now)),
      ate: toTimestamp(endOfDay(now)),
    };
  }

  if (mode === 'last7') {
    const ate = now;
    const de = new Date(now);
    de.setDate(de.getDate() - 6);
    return {
      mode,
      de: toTimestamp(startOfDay(de)),
      ate: toTimestamp(endOfDay(ate)),
    };
  }

  if (mode === 'month') {
    const de = new Date(now.getFullYear(), now.getMonth(), 1);
    const ate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      mode,
      de: toTimestamp(startOfDay(de)),
      ate: toTimestamp(endOfDay(ate)),
    };
  }

  if (mode === 'last3') {
    const ate = now;
    const de = new Date(now.getFullYear(), now.getMonth() - 2, 1);
    return {
      mode,
      de: toTimestamp(startOfDay(de)),
      ate: toTimestamp(endOfDay(ate)),
    };
  }

  if (mode === 'last12') {
    const ate = now;
    const de = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    return {
      mode,
      de: toTimestamp(startOfDay(de)),
      ate: toTimestamp(endOfDay(ate)),
    };
  }

  if (mode === 'custom') {
    const deD = from ? toDateSafe(from) : new Date(now.getFullYear(), 0, 1);
    const ateD = to ? toDateSafe(to) : now;
    return {
      mode,
      de: toTimestamp(startOfDay(deD || now)),
      ate: toTimestamp(endOfDay(ateD || now)),
    };
  }

  // default: year
  const y = Number(year || now.getFullYear());
  const de = new Date(y, 0, 1);
  const ate = new Date(y, 11, 31);
  return {
    mode: 'year',
    year: y,
    de: toTimestamp(startOfDay(de)),
    ate: toTimestamp(endOfDay(ate)),
  };
}

/**
 * Cria período comparativo padrão (período anterior com mesma duração)
 * Ex: "Este mês" -> "Mês passado"
 * Ex: "Últimos 7 dias" -> "7 dias anteriores"
 */
export function buildComparePeriod(period) {
  const de = period?.de?.toDate ? period.de.toDate() : new Date();
  const ate = period?.ate?.toDate ? period.ate.toDate() : new Date();

  const days = diffDays(de, ate) + 1;
  const prevEnd = new Date(de);
  prevEnd.setDate(prevEnd.getDate() - 1);

  const prevStart = new Date(prevEnd);
  prevStart.setDate(prevStart.getDate() - (days - 1));

  return {
    mode: 'compare',
    de: toTimestamp(startOfDay(prevStart)),
    ate: toTimestamp(endOfDay(prevEnd)),
  };
}

// Labels tipo "Out/25" pro eixo do gráfico
function monthLabel(yyyyMM) {
  const [y, m] = String(yyyyMM).split('-');
  const meses = [
    'Jan',
    'Fev',
    'Mar',
    'Abr',
    'Mai',
    'Jun',
    'Jul',
    'Ago',
    'Set',
    'Out',
    'Nov',
    'Dez',
  ];
  const idx = Number(m) - 1;
  const yy = String(y).slice(-2);
  return `${meses[idx]}/${yy}`;
}

function parseValor(val) {
  if (val === null || val === undefined || val === '') return 0;
  if (typeof val === 'number') return Number.isFinite(val) ? val : 0;

  const s = String(val).trim();
  const cleaned = s.replace(/[^\d.,-]/g, '');

  if (cleaned.includes(',')) {
    const normalized = cleaned.replace(/\./g, '').replace(',', '.');
    const n = Number(normalized);
    return Number.isFinite(n) ? n : 0;
  }

  const normalized = cleaned.replace(/\./g, '');
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}

function chunk10(arr) {
  const out = [];
  for (let i = 0; i < arr.length; i += 10) out.push(arr.slice(i, i + 10));
  return out;
}

/* ============================================================
   DISPONÍVEL PARA VENDA
============================================================ */

function isDisponivelParaVenda(imovel) {
  const statusNegocio = String(imovel?.statusNegocio || '').toLowerCase();
  const status = String(imovel?.status || '').toLowerCase();

  const bloqueados = new Set([
    'vendido',
    'encerrado',
    'inativo',
    'cancelado',
    'pausado',
    'arquivado',
    'retirado',
  ]);

  if (bloqueados.has(status) || bloqueados.has(statusNegocio)) return false;

  if (status === 'publicado') return true;

  if (imovel?.disponivel === true) return true;
  if (statusNegocio === 'ativo') return true;
  if (status === 'disponivel') return true;

  if (!status && !statusNegocio) return true;

  return false;
}

/* ============================================================
   SÓCIOS ACEITOS
============================================================ */

async function listarSociosAceitosUids(uid) {
  if (!uid) return [];

  try {
    const q1 = query(
      collection(db, 'socios'),
      where('uid1', '==', uid),
      where('status', '==', 'aceito')
    );

    const q2 = query(
      collection(db, 'socios'),
      where('uid2', '==', uid),
      where('status', '==', 'aceito')
    );

    const [s1, s2] = await Promise.all([getDocs(q1), getDocs(q2)]);

    const uids = new Set();

    s1.forEach((d) => {
      const data = d.data();
      if (data?.uid2) uids.add(data.uid2);
    });

    s2.forEach((d) => {
      const data = d.data();
      if (data?.uid1) uids.add(data.uid1);
    });

    return [...uids];
  } catch (err) {
    console.error('Erro ao listar sócios aceitos:', err);
    return [];
  }
}

/* ============================================================
   FUNÇÕES DO PAINEL ADMIN
============================================================ */

export async function contarUsuarios() {
  try {
    const snap = await getDocs(collection(db, 'usuarios'));
    return snap.size;
  } catch (err) {
    console.error('Erro em contarUsuarios:', err);
    return 0;
  }
}

export async function contarImoveis() {
  try {
    const snap = await getDocs(collection(db, 'imoveis'));
    return snap.size;
  } catch (err) {
    console.error('Erro em contarImoveis:', err);
    return 0;
  }
}

export async function contarImoveisPendentes() {
  try {
    const qy = query(
      collection(db, 'imoveis'),
      where('status', '==', 'pendente')
    );
    const snap = await getDocs(qy);
    return snap.size;
  } catch (err) {
    console.error('Erro em contarImoveisPendentes:', err);
    return 0;
  }
}

/* ============================================================
   CONTADORES (DASH DO CORRETOR)
============================================================ */

export async function contarAnunciosAtivos(uid) {
  if (!uid) return 0;

  try {
    const socios = await listarSociosAceitosUids(uid);
    const owners = Array.from(new Set([uid, ...socios]));

    const chunks = chunk10(owners);
    const snaps = await Promise.all(
      chunks.map((c) =>
        getDocs(query(collection(db, 'imoveis'), where('ownerId', 'in', c)))
      )
    );

    let count = 0;
    const ids = new Set();

    snaps.forEach((snap) => {
      snap.forEach((doc) => {
        if (ids.has(doc.id)) return;
        ids.add(doc.id);

        const imovel = doc.data();
        if (isDisponivelParaVenda(imovel)) count += 1;
      });
    });

    return count;
  } catch (err) {
    console.error('Erro em contarAnunciosAtivos (disponíveis):', err);
    return 0;
  }
}

export async function contarImoveisComPlaca(uid) {
  if (!uid) return 0;

  try {
    const socios = await listarSociosAceitosUids(uid);
    const owners = Array.from(new Set([uid, ...socios]));

    const chunks = chunk10(owners);
    const snaps = await Promise.all(
      chunks.map((c) =>
        getDocs(
          query(
            collection(db, 'imoveis'),
            where('ownerId', 'in', c),
            where('temPlaca', '==', true)
          )
        )
      )
    );

    let count = 0;
    const ids = new Set();

    snaps.forEach((snap) => {
      snap.forEach((doc) => {
        if (ids.has(doc.id)) return;
        ids.add(doc.id);

        const imovel = doc.data();
        if (isDisponivelParaVenda(imovel)) count += 1;
      });
    });

    return count;
  } catch (err) {
    console.error('Erro em contarImoveisComPlaca:', err);
    return 0;
  }
}

/**
 * ✅ Negócios por status — pode filtrar por período (createdAt)
 * OBS: se o doc não tiver createdAt, cai no modo legado (sem filtro).
 */
export async function contarNegociosPorStatus(uid, { period } = {}) {
  if (!uid) return {};

  try {
    const base = [where('corretorId', '==', uid)];

    if (period?.de && period?.ate) {
      base.push(where('createdAt', '>=', period.de));
      base.push(where('createdAt', '<=', period.ate));
    }

    const qy = query(collection(db, 'negocios'), ...base);
    const snap = await getDocs(qy);

    const map = {};
    snap.forEach((d) => {
      const s = d.data().statusPipeline || 'contato';
      map[s] = (map[s] || 0) + 1;
    });

    return map;
  } catch (err) {
    // fallback legado (sem createdAt)
    try {
      const qy = query(
        collection(db, 'negocios'),
        where('corretorId', '==', uid)
      );
      const snap = await getDocs(qy);

      const map = {};
      snap.forEach((d) => {
        const s = d.data().statusPipeline || 'contato';
        map[s] = (map[s] || 0) + 1;
      });

      return map;
    } catch (err2) {
      console.error('Erro em contarNegociosPorStatus:', err2);
      return {};
    }
  }
}

/**
 * ✅ VISITAS (Modelo atual do Elion)
 *
 * Seu Firestore (negocios) tem:
 * - visitasRealizadasCount: number
 * - ultimaVisitaRealizadaEm: Timestamp (quando atualiza visita)
 *
 * Então:
 * - Para período: filtra por ultimaVisitaRealizadaEm
 * - Soma visitasRealizadasCount
 *
 * Fallbacks:
 * - se não houver indice/campo: busca todos negócios do corretor e soma
 */
export async function contarVisitasRealizadas(uid, { period } = {}) {
  if (!uid) return 0;

  try {
    const base = [where('corretorId', '==', uid)];

    // ✅ filtro por "ultimaVisitaRealizadaEm" (o que você tem no print)
    if (period?.de && period?.ate) {
      base.push(where('ultimaVisitaRealizadaEm', '>=', period.de));
      base.push(where('ultimaVisitaRealizadaEm', '<=', period.ate));
    }

    let snap;
    try {
      snap = await getDocs(query(collection(db, 'negocios'), ...base));
    } catch (e) {
      // sem índice / sem campo -> legado total
      snap = await getDocs(
        query(collection(db, 'negocios'), where('corretorId', '==', uid))
      );
    }

    let total = 0;
    snap.forEach((d) => {
      const data = d.data() || {};
      const n = Number(data.visitasRealizadasCount || 0);
      total += Number.isFinite(n) ? n : 0;
    });

    return total;
  } catch (err) {
    console.error('Erro em contarVisitasRealizadas:', err);
    return 0;
  }
}

/* ============================================================
   VENDAS POR MÊS (VGV + COMISSÃO)
============================================================ */

export async function listarVendasPorMes(uid, opts = {}) {
  if (!uid) return [];

  try {
    const period = opts?.mode
      ? buildPeriod(opts)
      : typeof opts?.meses === 'number'
        ? buildPeriod({ mode: 'last12' })
        : buildPeriod({ mode: 'year' });

    const { de, ate } = period;

    const start = de.toDate();
    const end = ate.toDate();
    const buckets = {};

    const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    while (cursor <= end) {
      const key = getMonthKey(cursor);
      buckets[key] = {
        mes: key,
        label: monthLabel(key),
        vgv: 0,
        comissao: 0,
        quantidade: 0,
      };
      cursor.setMonth(cursor.getMonth() + 1);
    }

    const qVendas = query(
      collection(db, 'vendas'),
      where('uid', '==', uid),
      where('data', '>=', de),
      where('data', '<=', ate)
    );

    const snap = await getDocs(qVendas);

    snap.forEach((docSnap) => {
      const v = docSnap.data() || {};
      const dt = toDateSafe(v.data || v.dataVenda);
      if (!dt) return;

      const key = getMonthKey(dt);
      if (!key || !buckets[key]) return;

      buckets[key].vgv += parseValor(v.valor);
      buckets[key].comissao += parseValor(v.comissao);
      buckets[key].quantidade += 1;
    });

    return Object.values(buckets).sort((a, b) => a.mes.localeCompare(b.mes));
  } catch (err) {
    console.error('Erro em listarVendasPorMes:', err);
    return [];
  }
}

/* ============================================================
   VENDAS RECENTES (TABELA)
============================================================ */

export async function listarVendasRecentes(
  uid,
  { limit: max = 50, period } = {}
) {
  if (!uid) return [];

  const limiteQuery = Number.isFinite(max) && max > 0 ? max : 50;

  // ✅ FIX: se period já veio com de/ate, usa ele.
  const p = period?.de && period?.ate ? period : buildPeriod({ mode: 'year' });

  try {
    const qVendas = query(
      collection(db, 'vendas'),
      where('uid', '==', uid),
      where('data', '>=', p.de),
      where('data', '<=', p.ate),
      orderBy('data', 'desc'),
      limit(limiteQuery)
    );

    const snap = await getDocs(qVendas);

    return snap.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));
  } catch (err) {
    console.error('Erro em listarVendasRecentes (com orderBy):', err);

    // fallback: sem orderBy
    try {
      const qFallback = query(
        collection(db, 'vendas'),
        where('uid', '==', uid),
        where('data', '>=', p.de),
        where('data', '<=', p.ate),
        limit(limiteQuery)
      );

      const snap = await getDocs(qFallback);

      const items = snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      items.sort((a, b) => {
        const da = toDateSafe(a?.data) || toDateSafe(a?.dataVenda);
        const dbb = toDateSafe(b?.data) || toDateSafe(b?.dataVenda);
        const ta = da ? da.getTime() : 0;
        const tb = dbb ? dbb.getTime() : 0;
        return tb - ta;
      });

      return items;
    } catch (err2) {
      console.error('Erro em listarVendasRecentes (fallback):', err2);
      return [];
    }
  }
}

/* ============================================================
   ✅ NOVO — ANOS DISPONÍVEIS (HISTÓRICO FINANCEIRO)
============================================================ */

export async function listarAnosDisponiveisVendas(uid) {
  if (!uid) return [];

  const toYear = (v) => {
    const d = toDateSafe(v);
    if (!d) return null;
    return d.getFullYear();
  };

  try {
    const qMin = query(
      collection(db, 'vendas'),
      where('uid', '==', uid),
      orderBy('data', 'asc'),
      limit(1)
    );

    const qMax = query(
      collection(db, 'vendas'),
      where('uid', '==', uid),
      orderBy('data', 'desc'),
      limit(1)
    );

    const [sMin, sMax] = await Promise.all([getDocs(qMin), getDocs(qMax)]);

    if (sMin.empty || sMax.empty) return [];

    const minDoc = sMin.docs[0]?.data?.() || {};
    const maxDoc = sMax.docs[0]?.data?.() || {};

    const minYear = toYear(minDoc?.data || minDoc?.dataVenda);
    const maxYear = toYear(maxDoc?.data || maxDoc?.dataVenda);

    if (!minYear || !maxYear) return [];

    const years = [];
    for (let y = minYear; y <= maxYear; y += 1) years.push(y);
    return years;
  } catch (err) {
    try {
      const qAll = query(
        collection(db, 'vendas'),
        where('uid', '==', uid),
        limit(300)
      );
      const snap = await getDocs(qAll);

      const set = new Set();
      snap.forEach((d) => {
        const data = d.data?.() || {};
        const y = toYear(data?.data || data?.dataVenda);
        if (y) set.add(y);
      });

      return [...set].sort((a, b) => a - b);
    } catch (err2) {
      console.error('Erro em listarAnosDisponiveisVendas:', err2);
      return [];
    }
  }
}

/* ============================================================
   ✅ NOVO — MOVIMENTO POR MÊS (LINHAS)
   (Placas, Imóveis, Negócios, Visitas, Vendas)
============================================================ */

const MESES_PT = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

function safeIncMonth(arr12, dateObj) {
  if (!dateObj) return;
  const idx = dateObj.getMonth();
  if (idx >= 0 && idx <= 11) arr12[idx] += 1;
}

/**
 * ✅ Retorna 12 meses (Jan..Dez) com:
 * { mes, placas, imoveis, negocios, visitas, vendas }
 *
 * ✅ VISITAS:
 * - NÃO usa coleção `visitas` (porque você não tem)
 * - usa `negocios.ultimaVisitaRealizadaEm` + soma `visitasRealizadasCount`
 */
export async function listarMovimentoPorMes(uid, { from, to, year } = {}) {
  if (!uid) return [];

  const y = Number(year);
  const hasYear = Number.isFinite(y);

  const fromD = from
    ? toDateSafe(from)
    : hasYear
      ? new Date(y, 0, 1, 0, 0, 0)
      : null;

  const toD = to
    ? toDateSafe(to)
    : hasYear
      ? new Date(y, 11, 31, 23, 59, 59)
      : null;

  const mk12 = () => new Array(12).fill(0);

  const imoveis12 = mk12();
  const placas12 = mk12();
  const negocios12 = mk12();
  const visitas12 = mk12();
  const vendas12 = mk12();

  // 1) IMÓVEIS + PLACAS — ✅ incluir sócios aceitos (ownerId in [uid,...socios])
  try {
    const socios = await listarSociosAceitosUids(uid);
    const owners = Array.from(new Set([uid, ...socios]));
    const chunks = chunk10(owners);

    const snaps = await Promise.all(
      chunks.map((c) =>
        getDocs(query(collection(db, 'imoveis'), where('ownerId', 'in', c)))
      )
    );

    snaps.forEach((snapImoveis) => {
      snapImoveis.forEach((docSnap) => {
        const im = docSnap.data() || {};
        const dt = toDateSafe(
          im.criadoEm || im.createdAt || im.updatedAt || im.atualizadoEm
        );
        if (!dt) return;
        if (fromD && dt < fromD) return;
        if (toD && dt > toD) return;

        safeIncMonth(imoveis12, dt);

        if (im.temPlaca === true && isDisponivelParaVenda(im)) {
          safeIncMonth(placas12, dt);
        }
      });
    });
  } catch (e) {
    console.warn(
      '[dashboardService] listarMovimentoPorMes: imoveis/placas:',
      e
    );
  }

  // 2) NEGÓCIOS (negocios.corretorId, createdAt) — fallback: sem range se createdAt não existir
  try {
    let snapNeg = null;

    if (fromD && toD) {
      try {
        snapNeg = await getDocs(
          query(
            collection(db, 'negocios'),
            where('corretorId', '==', uid),
            where('createdAt', '>=', toTimestamp(fromD)),
            where('createdAt', '<=', toTimestamp(toD))
          )
        );
      } catch {
        snapNeg = null;
      }
    }

    if (!snapNeg) {
      snapNeg = await getDocs(
        query(collection(db, 'negocios'), where('corretorId', '==', uid))
      );
    }

    snapNeg.forEach((docSnap) => {
      const n = docSnap.data() || {};
      const dt =
        toDateSafe(n.createdAt) ||
        toDateSafe(n.criadoEm) ||
        toDateSafe(n.data) ||
        null;

      if (!dt) return;
      if (fromD && dt < fromD) return;
      if (toD && dt > toD) return;

      safeIncMonth(negocios12, dt);
    });
  } catch (e) {
    console.warn('[dashboardService] listarMovimentoPorMes: negocios:', e);
  }

  // 3) VISITAS (✅ via negocios.ultimaVisitaRealizadaEm + soma visitasRealizadasCount)
  try {
    let snapVisNeg = null;

    // Se tiver range, tenta filtrar por ultimaVisitaRealizadaEm
    if (fromD && toD) {
      try {
        snapVisNeg = await getDocs(
          query(
            collection(db, 'negocios'),
            where('corretorId', '==', uid),
            where('ultimaVisitaRealizadaEm', '>=', toTimestamp(fromD)),
            where('ultimaVisitaRealizadaEm', '<=', toTimestamp(toD))
          )
        );
      } catch {
        snapVisNeg = null;
      }
    }

    // fallback: sem filtro por data
    if (!snapVisNeg) {
      snapVisNeg = await getDocs(
        query(collection(db, 'negocios'), where('corretorId', '==', uid))
      );
    }

    snapVisNeg.forEach((docSnap) => {
      const n = docSnap.data() || {};
      const dt = toDateSafe(n.ultimaVisitaRealizadaEm);
      if (!dt) return;

      if (fromD && dt < fromD) return;
      if (toD && dt > toD) return;

      // ✅ adiciona N visitas no mês (não só +1)
      const qtd = Number(n.visitasRealizadasCount || 0);
      const inc = Number.isFinite(qtd) ? qtd : 0;

      const idx = dt.getMonth();
      if (idx >= 0 && idx <= 11) visitas12[idx] += inc;
    });
  } catch (e) {
    console.warn('[dashboardService] listarMovimentoPorMes: visitas:', e);
  }

  // 4) VENDAS — ✅ FIX: query já filtrada por range quando existir
  let vendasDaColecao = 0;
  try {
    const qVendas =
      fromD && toD
        ? query(
            collection(db, 'vendas'),
            where('uid', '==', uid),
            where('data', '>=', toTimestamp(fromD)),
            where('data', '<=', toTimestamp(toD))
          )
        : query(collection(db, 'vendas'), where('uid', '==', uid));

    const snapVendas = await getDocs(qVendas);

    snapVendas.forEach((docSnap) => {
      const vd = docSnap.data() || {};
      const dt = toDateSafe(vd.data || vd.dataVenda);
      if (!dt) return;
      if (fromD && dt < fromD) return;
      if (toD && dt > toD) return;

      safeIncMonth(vendas12, dt);
      vendasDaColecao += 1;
    });
  } catch (e) {
    console.warn(
      '[dashboardService] listarMovimentoPorMes: vendas (coleção):',
      e
    );
  }

  // 4b) fallback via imoveis.statusNegocio === 'vendido' + vendaInfo.data
  if (vendasDaColecao === 0) {
    try {
      const snapImoveis = await getDocs(
        query(collection(db, 'imoveis'), where('ownerId', '==', uid))
      );

      snapImoveis.forEach((docSnap) => {
        const im = docSnap.data() || {};
        const statusNegocio = String(im.statusNegocio || '').toLowerCase();
        if (statusNegocio !== 'vendido') return;

        const dt =
          toDateSafe(im?.vendaInfo?.data) || toDateSafe(im.updatedAt) || null;
        if (!dt) return;
        if (fromD && dt < fromD) return;
        if (toD && dt > toD) return;

        safeIncMonth(vendas12, dt);
      });
    } catch (e) {
      console.warn(
        '[dashboardService] listarMovimentoPorMes: vendas (fallback imoveis):',
        e
      );
    }
  }

  return MESES_PT.map((mes, idx) => ({
    mes,
    placas: placas12[idx] || 0,
    imoveis: imoveis12[idx] || 0,
    negocios: negocios12[idx] || 0,
    visitas: visitas12[idx] || 0,
    vendas: vendas12[idx] || 0,
  }));
}

/* ============================================================
   DASHBOARD PROFISSIONAL (PERÍODO + COMPARAÇÃO)
============================================================ */

/**
 * Retorna KPIs financeiros do período (VGV, comissão, quantidade de vendas)
 * + comparação com período anterior (ou comparePeriod informado)
 */
export async function getFinanceKpisComparativo(
  uid,
  { period, comparePeriod } = {}
) {
  const p = period?.de ? period : buildPeriod({ mode: 'month' });
  const c = comparePeriod?.de ? comparePeriod : buildComparePeriod(p);

  const [curMensal, prevMensal] = await Promise.all([
    listarVendasPorMes(uid, {
      mode: 'custom',
      from: p.de.toDate(),
      to: p.ate.toDate(),
    }),
    listarVendasPorMes(uid, {
      mode: 'custom',
      from: c.de.toDate(),
      to: c.ate.toDate(),
    }),
  ]);

  const sum = (arr, key) =>
    (arr || []).reduce((acc, m) => acc + Number(m?.[key] || 0), 0);

  const curVgv = sum(curMensal, 'vgv');
  const prevVgv = sum(prevMensal, 'vgv');

  const curCom = sum(curMensal, 'comissao');
  const prevCom = sum(prevMensal, 'comissao');

  const curQtd = sum(curMensal, 'quantidade');
  const prevQtd = sum(prevMensal, 'quantidade');

  return {
    period: p,
    comparePeriod: c,
    vgv: calcDelta(curVgv, prevVgv),
    comissao: calcDelta(curCom, prevCom),
    quantidadeVendas: calcDelta(curQtd, prevQtd),
  };
}

/**
 * Funil do período:
 * - Negócios criados no período
 * - Visitas no período (✅ via negocios.ultimaVisitaRealizadaEm + visitasRealizadasCount)
 * - Contratos do período (proxy = vendas)
 */
export async function getFunilPeriodo(uid, { period } = {}) {
  const p = period?.de ? period : buildPeriod({ mode: 'month' });

  // negócios criados no período (tenta createdAt)
  let negociosCriados = 0;
  try {
    const qNeg = query(
      collection(db, 'negocios'),
      where('corretorId', '==', uid),
      where('createdAt', '>=', p.de),
      where('createdAt', '<=', p.ate)
    );
    const snap = await getDocs(qNeg);
    negociosCriados = snap.size;
  } catch (e) {
    // fallback: sem createdAt (modo legado)
    const qNeg = query(
      collection(db, 'negocios'),
      where('corretorId', '==', uid)
    );
    const snap = await getDocs(qNeg);
    negociosCriados = snap.size;
  }

  // ✅ visitas no período (novo modelo)
  const visitasNoPeriodo = await contarVisitasRealizadas(uid, { period: p });

  // contratos no período (proxy: quantidade de vendas no período)
  const vendasMensal = await listarVendasPorMes(uid, {
    mode: 'custom',
    from: p.de.toDate(),
    to: p.ate.toDate(),
  });
  const contratosNoPeriodo = (vendasMensal || []).reduce(
    (acc, m) => acc + Number(m?.quantidade || 0),
    0
  );

  // conversões básicas (protege /0)
  const convNegToVis =
    negociosCriados > 0 ? (visitasNoPeriodo / negociosCriados) * 100 : 0;
  const convVisToCon =
    visitasNoPeriodo > 0 ? (contratosNoPeriodo / visitasNoPeriodo) * 100 : 0;

  return {
    period: p,
    negociosCriados,
    visitasNoPeriodo,
    contratosNoPeriodo,
    // ✅ agora não é mais fallback: é fonte oficial (negocios)
    visitasIsFallback: false,
    conversoes: {
      negociosParaVisitasPct: Number(convNegToVis.toFixed(1)),
      visitasParaContratosPct: Number(convVisToCon.toFixed(1)),
    },
  };
}

/**
 * Retorna os KPIs principais comparativos (cards):
 * - VGV do período
 * - Comissão do período
 * - Negócios criados no período
 * - Visitas no período
 * - Contratos no período (proxy = vendas)
 */
export async function getCardsComparativos(
  uid,
  { period, comparePeriod } = {}
) {
  const p = period?.de ? period : buildPeriod({ mode: 'month' });
  const c = comparePeriod?.de ? comparePeriod : buildComparePeriod(p);

  const [fin, funilCur, funilPrev] = await Promise.all([
    getFinanceKpisComparativo(uid, { period: p, comparePeriod: c }),
    getFunilPeriodo(uid, { period: p }),
    getFunilPeriodo(uid, { period: c }),
  ]);

  return {
    period: p,
    comparePeriod: c,
    cards: {
      vgv: fin.vgv,
      comissao: fin.comissao,
      negociosCriados: calcDelta(
        funilCur.negociosCriados,
        funilPrev.negociosCriados
      ),
      visitas: calcDelta(funilCur.visitasNoPeriodo, funilPrev.visitasNoPeriodo),
      contratos: calcDelta(
        funilCur.contratosNoPeriodo,
        funilPrev.contratosNoPeriodo
      ),
    },
    flags: {
      visitasIsFallback: false,
    },
  };
}
